import React, { useContext } from 'react'
import { Container } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { ArrowBackIos, ArrowForwardIos } from '@mui/icons-material'
import loadable from '@loadable/component'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import { CarouselBreakpoint } from '../../types/landingPage'
import useBrand from '../../hooks/useBrand'
import { ProgressBarContext } from '../../components/Reviews/ReviewSection'

const Slider = loadable(() => import('react-slick'))

interface Props {
  children: React.ReactNode
  slidesToShow: number
  arrowClass?: string
  arrowContainerClass?: string
  arrowLeftClass?: string
  arrowRightClass?: string
  arrowContainerLeftClass?: string
  arrowContainerRightClass?: string
  arrows?: boolean
  dots?: boolean
  rows?: number
  autoplay?: boolean
  autoplaySpeed?: number
  dotsClass?: string
  responsiveSettings?: CarouselBreakpoint[] | null
  infinite?: boolean // Add this line
  rtl?: boolean // Add this line
}

const useStyles = makeStyles()((theme) => ({
  arrow: {
    position: 'absolute',
    transform: 'translate(0, -50%)',
    '&&': {
      fontSize: '0',
      height: '1.5rem',
      left: 'initial',
      right: '.85rem',
      top: '50%',
      width: '1.5rem',

      [theme.breakpoints.down('md')]: {
        height: '1.25rem',
        right: '.3rem',
        width: '1.25rem',
      },
    },
  },
  arrowTR: {
    '&&': {
      right: '.55rem',
    },
  },
  arrowContainer: {
    backgroundColor: theme.palette.background.default,
    borderRadius: '100%',
    zIndex: 100,

    '&&': {
      border: `1px solid ${theme.palette.divider}`,
      color: theme.palette.primary.main,
      fontSize: '0',
      height: '3.5rem',
      width: '3.5rem',

      '&:hover': {
        backgroundColor: theme.palette.action.hover,
        borderColor: theme.palette.action.hover,
        color: theme.palette.background.default,
      },

      [theme.breakpoints.down('md')]: {
        height: '2.125rem',
        width: '2.125rem',
      },
    },
    '&:before': {
      display: 'none',
    },
  },
  arrowContainerTR: {
    '&&': {
      backgroundColor: theme.palette.divider,
      color: theme.colors.gray['a400'],
      height: '3rem',
      width: '3rem',

      '&:active': {
        backgroundColor: theme.colors.gray['a200'],
        borderColor: theme.colors.gray['a200'],
        color: theme.palette.primary.main,
      },
      '&:hover': {
        backgroundColor: theme.palette.divider,
        borderColor: theme.palette.divider,
        color: theme.palette.primary.main,
      },
    },
  },
  arrowContainerLeft: {
    '&&': {
      left: '1.56rem',
    },
  },
  arrowContainerRight: {
    '&&': {
      right: '1.56rem',
    },
  },
  arrowLeft: {
    '&&': {
      left: '1.25rem',
      right: 'initial',

      [theme.breakpoints.down('md')]: {
        left: '.65rem',
      },
    },
  },
  arrowLeftTR: {
    '&&': {
      left: '1rem',
    },
  },
  breadcrumbs: {
    '&&': {
      bottom: '2.1875rem',

      '& li': {
        margin: '0',
        height: '1rem',
        width: '1rem',
        '& button': {
          '&:before': {
            color: `${theme.palette.primary.main} !important`,
            fontSize: '.5rem',
          },
        },
      },

      [theme.breakpoints.down('md')]: {
        bottom: '0',
      },
    },
  },
}))

const responsiveSettingsDefault = [
  {
    breakpoint: 960,
    settings: {
      arrows: false,
    },
  },
]
/**
 * This component uses the minimum of slidesToShow and the number of children to avoid duplicating reviews
 * when children happen to be less than slidesToShow
 */
const Carousel: React.FC<Props> = ({
  slidesToShow,
  arrowClass = '',
  arrowContainerClass = '',
  arrowLeftClass = '',
  arrowRightClass = '',
  arrowContainerLeftClass = '',
  arrowContainerRightClass = '',
  arrows = true,
  dots = true,
  rows,
  autoplay,
  autoplaySpeed,
  dotsClass = '',
  responsiveSettings = null,
  children,
}) => {
  const { classes, cx } = useStyles()
  const brand = useBrand()
  const { setCurrentIndex } = useContext(ProgressBarContext) || {}

  const settings = {
    centerMode: false,
    arrows,
    dots,
    dotsClass: cx('slick-dots', classes.breadcrumbs, dotsClass),
    infinite: true,
    beforeChange: (oldIndex: number, newIndex: number) => {
      setCurrentIndex && setCurrentIndex(newIndex)
    },
    rows: rows || 1,
    nextArrow: (
      <Container
        classes={{
          root: cx({
            [classes.arrowContainer]: true,
            [classes.arrowContainerRight]: true,
            [classes.arrowContainerTR]: brand.name !== 'ZipJob',
            [arrowContainerClass]: arrowContainerClass !== '',
            [arrowContainerRightClass]: arrowContainerRightClass !== '',
            [arrowContainerRightClass]: arrowContainerRightClass !== '',
          }),
        }}
        disableGutters
      >
        <ArrowForwardIos
          classes={{
            root: cx({
              [classes.arrow]: true,
              [classes.arrowTR]: brand.name !== 'ZipJob',
              [arrowClass]: arrowClass !== '',
              [arrowRightClass]: arrowRightClass !== '',
            }),
          }}
        />
      </Container>
    ),
    prevArrow: (
      <Container
        classes={{
          root: cx({
            [classes.arrowContainer]: true,
            [classes.arrowContainerLeft]: true,
            [classes.arrowContainerTR]: brand.name !== 'ZipJob',
            [arrowContainerClass]: arrowContainerClass !== '',
            [arrowContainerLeftClass]: arrowContainerLeftClass !== '',
          }),
        }}
        disableGutters
      >
        <ArrowBackIos
          classes={{
            root: cx({
              [classes.arrow]: true,
              [classes.arrowLeft]: true,
              [classes.arrowLeftTR]: brand.name !== 'ZipJob',
              [arrowClass]: arrowClass !== '',
              [arrowLeftClass]: arrowLeftClass !== '',
            }),
          }}
        />
      </Container>
    ),
    speed: 500,
    slidesToScroll: 1,
    slidesToShow: Math.min(React.Children.count(children), slidesToShow),
    autoplay,
    autoplaySpeed,
    responsive: responsiveSettings
      ? responsiveSettings
      : responsiveSettingsDefault,
    // rtl: true,
  }

  return <Slider {...settings}>{children}</Slider>
}

export default Carousel
